import React, { useContext } from 'react';
import WorkspaceContext from '../../Context/WorkspaceContext';
import { useSelector } from 'react-redux';
import { useRefFunction } from '../../../utils/hooks';
import { AlertType, IDispatchInstruction, ReportType } from '../../../graphql/schema/p4types';
import { IWorkspaceAction } from '../../../@types/workspaceTypes';
import { WorkspaceActionHeader } from '../ActionHeader';
import { IDeliveryNoteModalState, ILabelModalState } from './useDispatchPrintModals';
import { SpecialReportType, useP4DocumentController } from '../../document/DocumentProvider';
import { useActionCriteria } from '../../../utils/useActionProps';
import { IReduxStore } from '../../../@types/reduxTypes';
import { useP4ToastController } from '../../../views/system/NotificationsArea';

/**
 * Create ActionButtons for the print process *only*, i.e. the 'DispatchInstructions' workspace to re-print later.
 * @param setLabelModalState                  - Function to set receipt label state.
 * @param setDeliveryNoteModalState           - Function to set delivery note modal state.
 * @param setConformityCertificateModalState  - Function to set conformity certificate modal state.
 * @param actionEntityMapper                  - Optional function to map action entities (For label modal to function - Used by automatic dispatch results to map to dispatch instructions).
 */
export const useDispatchPrintButtons = (
  {
    setLabelModalState,
    setDeliveryNoteModalState,
    setConformityCertificateModalState,
  }: {
    setLabelModalState: React.Dispatch<React.SetStateAction<ILabelModalState>>;
    setDeliveryNoteModalState: React.Dispatch<React.SetStateAction<IDeliveryNoteModalState>>;
    setConformityCertificateModalState: React.Dispatch<React.SetStateAction<IDeliveryNoteModalState>>;
  },
  { actionEntityMapper }: { actionEntityMapper?: (actionEntities: Array<any>) => Array<IDispatchInstruction> } = {}
) => {
  const workspaceContext = useContext(WorkspaceContext);
  const { dispatchSimpleToast } = useP4ToastController();
  const { printDocumentActionEntities } = useP4DocumentController();
  const rawActionCriteria = useActionCriteria();
  const actionCriteria = React.useMemo(() => {
    // If we're on the "AutomaticDispatchResultWorkspace", we need to unwrap the actual dispatch instructions.
    // (If we're just on the regular Dispatch Instructions Workspace, we just fall back to the selected entity).
    const mappedActionEntities = rawActionCriteria.actionEntities.map(
      (actionEntity) => actionEntity.dispatchInstruction ?? actionEntity
    );
    return {
      ...rawActionCriteria,
      actionEntities: mappedActionEntities,
    };
  }, [rawActionCriteria]);

  const enableDocumentCarousel = useSelector((store: IReduxStore) => {
    return store.app.fullUserDetails?.flags?.documentCarouselEnabled ?? false;
  });

  const printLabelClick = useRefFunction(() => {
    if (!workspaceContext.selectionState?.selectedAll && workspaceContext.selectionState?.actionEntities?.length > 0) {
      if (enableDocumentCarousel) {
        printDocumentActionEntities(SpecialReportType.DispatchLabel, actionCriteria);
      } else {
        setLabelModalState({
          modalOpen: true,
          data: actionEntityMapper
            ? actionEntityMapper(workspaceContext.selectionState?.actionEntities || [])
            : workspaceContext.selectionState?.actionEntities,
        });
      }
    } else {
      dispatchSimpleToast(
        'Could not determine labels to print',
        'Please use individual selection boxes to choose dispatch instructions for label printing.',
        AlertType.Warning
      );
    }
  });

  const printButtonActions: Array<IWorkspaceAction> = [
    {
      key: 'printLabels',
      text: 'Print Labels',
      onClick: printLabelClick,
      actionReference: 'PRINT_DISPATCH_INSTRUCTIONS',
      header: WorkspaceActionHeader.REPORTS,
    },
    {
      key: 'printDeliveryNotes',
      text: 'Print Delivery Notes',
      onClick: () => {
        if (enableDocumentCarousel) {
          printDocumentActionEntities(ReportType.SalesDeliveryNote, actionCriteria);
        } else {
          setDeliveryNoteModalState({ modalOpen: true });
        }
      },
      actionReference: 'PRINT_DISPATCH_INSTRUCTIONS',
      header: WorkspaceActionHeader.REPORTS,
    },
    {
      key: 'printCertificatesOfConformity',
      text: 'Print Certificate of Conformity',
      onClick: () => {
        if (enableDocumentCarousel) {
          printDocumentActionEntities(ReportType.ConformityCertificate, actionCriteria);
        } else {
          setConformityCertificateModalState({ modalOpen: true });
        }
      },
      actionReference: 'PRINT_DISPATCH_INSTRUCTIONS',
      header: WorkspaceActionHeader.REPORTS,
    },
  ];
  return printButtonActions;
};
